import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Image as ImageIcon, PenTool as SignatureIcon } from 'lucide-react';
import { api } from '../../services/api';
import { useUserPrefs } from '../../Components/context/UserPrefs';
import SeguridadPendingExits from './SeguridadPendingExits';
import PreopPendingAuthorizations from './PreopPendingAuthorizations';
import PorteriaControl from './PorteriaControl';

export default function SeguridadPanel({ location, showLocation = true }) {
  const { currentUser } = useUserPrefs();
  const displayNames = {
    'popayan': 'Popayán',
    'santander-de-quilichao': 'Santander de Quilichao',
  };
  const title = displayNames[location] || location;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [statusMsg, setStatusMsg] = useState(null);
  const [exitEdits, setExitEdits] = useState({});
  const [exitSavingId, setExitSavingId] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState('');
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [cameraStage, setCameraStage] = useState('live');
  const [pendingPhotoUrl, setPendingPhotoUrl] = useState('');
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const videoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const photoInputRef = useRef(null);
  const sigCanvasRef = useRef(null);
  const sigDrawingRef = useRef(false);
  const sigLastRef = useRef({ x: 0, y: 0 });
  const [firmasInfo, setFirmasInfo] = useState({ visible: false, count: 0 });
  const [tallerInfo, setTallerInfo] = useState({ visible: false, count: 0 });
  const [form, setForm] = useState({
    visitor_name: '',
    visitor_dni: '',
    blood_type: '',
    contact_phone: '',
    contact_name: '',
    emergency_phone: '',
    eps: '',
    arl: '',
    area_ingreso: '',
    tarea_realizar: '',
    autoriza_entrada: '',
    photo: null,
    signature: null,
    entry_time: '',
    exit_time: '',
  });

  const nowLocalDateTime = useMemo(() => {
    const dt = new Date();
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    const hh = String(dt.getHours()).padStart(2, '0');
    const mm = String(dt.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mm}`;
  }, []);

  useEffect(() => {
    if (!form.entry_time) {
      setForm((p) => ({ ...p, entry_time: nowLocalDateTime }));
    }
  }, [nowLocalDateTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRows = async () => {
    setLoading(true);
    try {
      const res = await api.seguridad.visitors.list({ warehouse_id: location || '', q: query || '', date: entryDate || '', limit: 150 });
      const list = Array.isArray(res?.data) ? res.data : [];
      setRows(list);
    } catch (e) {
      setRows([]);
      setStatusMsg({ type: 'error', text: e?.message || 'Error cargando visitantes' });
      setTimeout(() => setStatusMsg(null), 2500);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const stopCamera = () => {
    try {
      const s = cameraStreamRef.current;
      if (s && typeof s.getTracks === 'function') {
        s.getTracks().forEach((t) => {
          try { t.stop(); } catch (_) {}
        });
      }
    } catch (_) {}
    cameraStreamRef.current = null;
    try {
      if (videoRef.current) videoRef.current.srcObject = null;
    } catch (_) {}
  };

  useEffect(() => () => {
    stopCamera();
    try { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); } catch (_) {}
    try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
    try { if (signaturePreviewUrl) URL.revokeObjectURL(signaturePreviewUrl); } catch (_) {}
  }, [photoPreviewUrl, pendingPhotoUrl, signaturePreviewUrl]);

  const openCamera = async () => {
    setCameraError('');
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Navegador sin soporte de cámara');
      }
      try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
      setPendingPhotoUrl('');
      setPendingPhotoFile(null);
      setCameraStage('live');
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      cameraStreamRef.current = stream;
      setCameraOpen(true);
      setTimeout(() => {
        try {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(() => {});
          }
        } catch (_) {}
      }, 0);
    } catch (e) {
      setCameraError(e?.message || 'No fue posible acceder a la cámara');
      try { if (photoInputRef.current) photoInputRef.current.click(); } catch (_) {}
    }
  };

  const capturePhoto = async () => {
    try {
      const v = videoRef.current;
      if (!v) return;
      const w = v.videoWidth || 1280;
      const h = v.videoHeight || 720;
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(v, 0, 0, w, h);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      if (!blob) return;
      const file = new File([blob], `foto-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopCamera();
      try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
      setPendingPhotoFile(file);
      setPendingPhotoUrl(URL.createObjectURL(file));
      setCameraStage('preview');
      // Queda registrada en el formulario apenas se captura, igual que
      // "Subir" — no depende de que el usuario recuerde dar clic en "Usar
      // foto" además. "Tomar otra"/"Eliminar" siguen disponibles por si no
      // le gustó el resultado.
      setForm((p) => ({ ...p, photo: file }));
      try { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); } catch (_) {}
      setPhotoPreviewUrl(URL.createObjectURL(file));
    } catch (e) {
      setCameraError(e?.message || 'No fue posible capturar la foto');
    }
  };

  const acceptPendingPhoto = () => {
    if (!pendingPhotoFile) return;
    const file = pendingPhotoFile;
    setForm((p) => ({ ...p, photo: file }));
    try { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); } catch (_) {}
    setPhotoPreviewUrl(URL.createObjectURL(file));
    try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
    setPendingPhotoUrl('');
    setPendingPhotoFile(null);
    setCameraOpen(false);
    setCameraStage('live');
  };

  const clearPhoto = () => {
    setForm((p) => ({ ...p, photo: null }));
    try { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); } catch (_) {}
    setPhotoPreviewUrl('');
    try { if (photoInputRef.current) photoInputRef.current.value = ''; } catch (_) {}
  };

  const clearSignature = () => {
    const c = sigCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    setForm((p) => ({ ...p, signature: null }));
    try { if (signaturePreviewUrl) URL.revokeObjectURL(signaturePreviewUrl); } catch (_) {}
    setSignaturePreviewUrl('');
  };

  const isCanvasBlank = (c) => {
    try {
      const ctx = c.getContext('2d');
      if (!ctx) return true;
      const { width, height } = c;
      if (!width || !height) return true;
      const step = 12;
      const img = ctx.getImageData(0, 0, width, height).data;
      for (let y = 0; y < height; y += step) {
        for (let x = 0; x < width; x += step) {
          const idx = ((y * width) + x) * 4;
          const a = img[idx + 3];
          if (a > 0) return false;
        }
      }
      return true;
    } catch (_) {
      return true;
    }
  };

  const loadSignatureToCanvas = async (url) => {
    const c = sigCanvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (c.width !== w || c.height !== h) {
      c.width = w;
      c.height = h;
    }
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    if (!url) return;
    await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(c.width / img.width, c.height / img.height);
          const dw = img.width * scale;
          const dh = img.height * scale;
          const dx = (c.width - dw) / 2;
          const dy = (c.height - dh) / 2;
          ctx.drawImage(img, dx, dy, dw, dh);
        } catch (_) {}
        resolve();
      };
      img.onerror = resolve;
      img.src = url;
    });
  };

  const syncSignatureFile = () => {
    const c = sigCanvasRef.current;
    if (!c) return;
    c.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], `firma-${Date.now()}.png`, { type: 'image/png' });
      setForm((p) => ({ ...p, signature: file }));
      try { if (signaturePreviewUrl) URL.revokeObjectURL(signaturePreviewUrl); } catch (_) {}
      setSignaturePreviewUrl(URL.createObjectURL(file));
    }, 'image/png');
  };

  const signaturePointerPos = (e) => {
    const c = sigCanvasRef.current;
    if (!c) return { x: 0, y: 0 };
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const x = (e.clientX - rect.left) * dpr;
    const y = (e.clientY - rect.top) * dpr;
    return { x, y };
  };

  const startSignature = (e) => {
    const c = sigCanvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.floor(rect.width * dpr));
    const h = Math.max(1, Math.floor(rect.height * dpr));
    if (c.width !== w || c.height !== h) {
      const prev = c.toDataURL('image/png');
      c.width = w;
      c.height = h;
      const ctx2 = c.getContext('2d');
      if (ctx2) {
        const img = new Image();
        img.onload = () => { try { ctx2.drawImage(img, 0, 0); } catch (_) {} };
        img.src = prev;
      }
    }
    sigDrawingRef.current = true;
    const p = signaturePointerPos(e);
    sigLastRef.current = p;
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.lineWidth = 2 * dpr;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#111827';
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    }
  };

  const moveSignature = (e) => {
    if (!sigDrawingRef.current) return;
    const c = sigCanvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const p = signaturePointerPos(e);
    const prev = sigLastRef.current;
    sigLastRef.current = p;
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (Math.abs(p.x - prev.x) + Math.abs(p.y - prev.y) > 0) {
      setForm((p0) => (p0.signature ? p0 : p0));
    }
  };

  const endSignature = () => {
    if (!sigDrawingRef.current) return;
    sigDrawingRef.current = false;
  };

  const openSignatureModal = async () => {
    setSignatureOpen(true);
    setTimeout(() => {
      loadSignatureToCanvas(signaturePreviewUrl || '');
    }, 0);
  };

  const saveSignature = () => {
    const c = sigCanvasRef.current;
    if (!c) { setSignatureOpen(false); return; }
    if (isCanvasBlank(c)) {
      clearSignature();
      setSignatureOpen(false);
      return;
    }
    syncSignatureFile();
    setSignatureOpen(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.seguridad.visitors.upsert({
        ...form,
        warehouse_id: location || '',
        created_by_admin_id: currentUser?.id || null,
        entry_time: form.entry_time ? form.entry_time.replace('T', ' ') + ':00' : '',
        exit_time: form.exit_time ? form.exit_time.replace('T', ' ') + ':00' : '',
      });
      setStatusMsg({ type: 'success', text: 'Visitante registrado correctamente' });
      setTimeout(() => setStatusMsg(null), 2500);
      setForm((p) => ({
        ...p,
        visitor_name: '',
        visitor_dni: '',
        blood_type: '',
        contact_phone: '',
        contact_name: '',
        emergency_phone: '',
        eps: '',
        arl: '',
        area_ingreso: '',
        tarea_realizar: '',
        autoriza_entrada: '',
        photo: null,
        signature: null,
        exit_time: '',
      }));
      try { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); } catch (_) {}
      setPhotoPreviewUrl('');
      try { if (signaturePreviewUrl) URL.revokeObjectURL(signaturePreviewUrl); } catch (_) {}
      setSignaturePreviewUrl('');
      loadRows();
    } catch (err) {
      setStatusMsg({ type: 'error', text: err?.message || 'No fue posible guardar el registro' });
      setTimeout(() => setStatusMsg(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  const formatDateTime = (v) => {
    const s = String(v || '').trim();
    if (!s) return '—';
    return s.replace('T', ' ').slice(0, 16);
  };

  const toDateTimeLocal = (v) => {
    const s = String(v || '').trim();
    if (!s) return '';
    const normalized = s.replace(' ', 'T');
    return normalized.slice(0, 16);
  };

  const saveExitTime = async (row) => {
    const raw = String(exitEdits[row.id] || '').trim();
    if (!raw) {
      setStatusMsg({ type: 'error', text: 'Seleccione la hora de salida' });
      setTimeout(() => setStatusMsg(null), 2000);
      return;
    }
    setExitSavingId(row.id);
    try {
      await api.seguridad.visitors.updateExit({
        id: row.id,
        exit_time: raw.replace('T', ' ') + ':00',
      });
      setStatusMsg({ type: 'success', text: 'Hora de salida actualizada' });
      setTimeout(() => setStatusMsg(null), 2000);
      await loadRows();
    } catch (e) {
      setStatusMsg({ type: 'error', text: e?.message || 'No fue posible actualizar salida' });
      setTimeout(() => setStatusMsg(null), 2500);
    } finally {
      setExitSavingId(null);
    }
  };

  return (
    <div className="text-gray-800">
      <h2 className="text-2xl font-semibold mb-2">Seguridad</h2>
      {showLocation && (
        <p className="text-gray-700">Sede: {title}</p>
      )}
      <div className="mt-4">
        {/* Todo el módulo en una sola barra de pestañas (Portería, firmas,
            visitantes): el vigilante no tiene que bajar a buscar nada. Las
            bandejas de firma solo aparecen a quien tiene ese rol. */}
        <PorteriaControl
          location={location}
          extraTabs={[
            { key: 'firmas', label: 'Preoperacionales por firmar', count: firmasInfo.count, hidden: !firmasInfo.visible, content: <PreopPendingAuthorizations location={location} embedded onInfo={setFirmasInfo} /> },
            { key: 'taller', label: 'Salidas de taller por autorizar', count: tallerInfo.count, hidden: !tallerInfo.visible, content: <SeguridadPendingExits location={location} embedded onInfo={setTallerInfo} /> },
            {
              key: 'ingresos',
              label: 'Historial de ingresos',
              content: (
                  <div className="xl:col-span-3 rounded-2xl p-[1px] bg-gradient-to-br from-indigo-100 via-white to-emerald-100 shadow-md">
                    <div className="bg-white rounded-2xl border border-gray-200 p-4">
                    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-800">Historial de Ingresos</div>
                        <div className="text-xs text-gray-500">Consulta y gestión rápida de ingresos registrados</div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <input
                          type="text"
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                          placeholder="Buscar nombre / cédula"
                        />
                          <input
                            type="date"
                            value={entryDate}
                            onChange={(e) => setEntryDate(e.target.value)}
                            className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
                            title="Fecha de entrada"
                          />
                        <button type="button" onClick={loadRows} className="px-4 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100">
                          Buscar
                        </button>
                      </div>
                    </div>
                    <div className="overflow-x-auto border rounded-xl">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="text-left p-2 border-b">Nombre</th>
                            <th className="text-left p-2 border-b">Cédula</th>
                            <th className="text-left p-2 border-b">Sangre</th>
                            <th className="text-left p-2 border-b">Tel. contacto</th>
                            <th className="text-left p-2 border-b">Contacto</th>
                            <th className="text-left p-2 border-b">Tel. emergencia</th>
                            <th className="text-left p-2 border-b">EPS</th>
                            <th className="text-left p-2 border-b">ARL</th>
                            <th className="text-left p-2 border-b">Área</th>
                            <th className="text-left p-2 border-b">Tarea</th>
                            <th className="text-left p-2 border-b">Autoriza</th>
                            <th className="text-left p-2 border-b">Foto</th>
                            <th className="text-left p-2 border-b">Entrada</th>
                            <th className="text-left p-2 border-b">Salida</th>
                            <th className="text-right p-2 border-b">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading && (
                            <tr><td colSpan={15} className="p-3 text-center text-gray-600">Cargando...</td></tr>
                          )}
                          {!loading && rows.length === 0 && (
                            <tr><td colSpan={15} className="p-3 text-center text-gray-600">Sin registros</td></tr>
                          )}
                          {!loading && rows.map((r) => (
                            <tr key={r.id} className="border-b hover:bg-gray-50/70">
                              <td className="p-2">{r.visitor_name}</td>
                              <td className="p-2">{r.visitor_dni}</td>
                              <td className="p-2">{r.blood_type || '—'}</td>
                              <td className="p-2">{r.contact_phone || '—'}</td>
                              <td className="p-2">{r.contact_name || '—'}</td>
                              <td className="p-2">{r.emergency_phone || '—'}</td>
                              <td className="p-2">{r.eps || '—'}</td>
                              <td className="p-2">{r.arl || '—'}</td>
                              <td className="p-2">{r.area_ingreso}</td>
                              <td className="p-2">{r.tarea_realizar || '—'}</td>
                              <td className="p-2">{r.autoriza_entrada}</td>
                              <td className="p-2">
                                {r.photo_url ? (
                                  <a href={r.photo_url} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline">
                                    Ver
                                  </a>
                                ) : '—'}
                              </td>
                              <td className="p-2 whitespace-nowrap">{formatDateTime(r.entry_time)}</td>
                              <td className="p-2 whitespace-nowrap">
                                <input
                                  type="datetime-local"
                                  value={exitEdits[r.id] ?? toDateTimeLocal(r.exit_time)}
                                  onChange={(e) => setExitEdits((p) => ({ ...p, [r.id]: e.target.value }))}
                                  className="border rounded px-2 py-1 text-xs"
                                />
                              </td>
                              <td className="p-2 text-right">
                                <div className="inline-flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => saveExitTime(r)}
                                    disabled={exitSavingId === r.id}
                                    className="w-20 h-8 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs hover:bg-emerald-100 disabled:opacity-60 inline-flex items-center justify-center text-center leading-tight"
                                  >
                                    {exitSavingId === r.id ? 'Guardando...' : 'Guardar salida'}
                                  </button>
                                  {r.photo_url ? (
                                    <a
                                      href={r.photo_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="w-20 h-8 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs hover:bg-indigo-100 inline-flex items-center justify-center"
                                      title="Ver foto"
                                    >
                                      <ImageIcon size={14} />
                                    </a>
                                  ) : (
                                    <span className="w-20 h-8 rounded-lg border border-gray-200 text-xs text-gray-400 inline-flex items-center justify-center bg-gray-50" title="Sin foto">
                                      <ImageIcon size={14} />
                                    </span>
                                  )}
                                  {r.signature_url ? (
                                    <a
                                      href={r.signature_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="w-20 h-8 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs hover:bg-indigo-100 inline-flex items-center justify-center"
                                      title="Ver firma"
                                    >
                                      <SignatureIcon size={14} />
                                    </a>
                                  ) : (
                                    <span className="w-20 h-8 rounded-lg border border-gray-200 text-xs text-gray-400 inline-flex items-center justify-center bg-gray-50" title="Sin firma">
                                      <SignatureIcon size={14} />
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    </div>
                  </div>
              ),
            },
            {
              key: 'visitantes',
              label: 'Ingreso de visitantes',
              content: (
                  <div className="xl:col-span-2 rounded-2xl p-[1px] bg-gradient-to-br from-emerald-100 via-white to-indigo-100 shadow-md">
                    <div className="bg-white rounded-2xl border border-gray-200 p-4">
                    <div className="mb-3">
                      <div className="text-sm font-semibold text-gray-800">Ingreso de Visitantes</div>
                      <div className="text-xs text-gray-500">Registro rápido y seguro de accesos</div>
                    </div>
                    <form className="space-y-3" onSubmit={handleSave}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={form.visitor_name}
                          onChange={(e) => setForm((p) => ({ ...p, visitor_name: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          placeholder="Nombre completo"
                          required
                        />
                        <input
                          type="text"
                          value={form.visitor_dni}
                          onChange={(e) => setForm((p) => ({ ...p, visitor_dni: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          placeholder="Cédula"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={form.blood_type}
                          onChange={(e) => setForm((p) => ({ ...p, blood_type: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          placeholder="Tipo de sangre (Ej: O+)"
                          required
                        />
                        <input
                          type="text"
                          value={form.contact_phone}
                          onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          placeholder="Teléfono contacto"
                          required
                        />
                        <input
                          type="text"
                          value={form.contact_name}
                          onChange={(e) => setForm((p) => ({ ...p, contact_name: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          placeholder="Nombre contacto"
                          required
                        />
                        <input
                          type="text"
                          value={form.emergency_phone}
                          onChange={(e) => setForm((p) => ({ ...p, emergency_phone: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          placeholder="Teléfono emergencia"
                          required
                        />
                        <input
                          type="text"
                          value={form.eps}
                          onChange={(e) => setForm((p) => ({ ...p, eps: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          placeholder="EPS"
                          required
                        />
                        <input
                          type="text"
                          value={form.arl}
                          onChange={(e) => setForm((p) => ({ ...p, arl: e.target.value }))}
                          className="w-full border rounded-lg px-3 py-2 text-sm sm:col-span-2 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          placeholder="ARL"
                          required
                        />
                      </div>
                      <input
                        type="text"
                        value={form.area_ingreso}
                        onChange={(e) => setForm((p) => ({ ...p, area_ingreso: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                        placeholder="Área al que ingresa"
                        required
                      />
                      <textarea
                        value={form.tarea_realizar}
                        onChange={(e) => setForm((p) => ({ ...p, tarea_realizar: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                        rows={2}
                        placeholder="Tarea a realizar"
                      />
                      <input
                        type="text"
                        value={form.autoriza_entrada}
                        onChange={(e) => setForm((p) => ({ ...p, autoriza_entrada: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                        placeholder="Quién autoriza la entrada"
                        required
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <div className="text-xs text-gray-600 mb-1">Foto</div>
                          <div className="flex gap-2 flex-wrap">
                            <input
                              ref={photoInputRef}
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                setForm((p) => ({ ...p, photo: file }));
                                try { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); } catch (_) {}
                                if (file) setPhotoPreviewUrl(URL.createObjectURL(file));
                                else setPhotoPreviewUrl('');
                                try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
                                setPendingPhotoUrl('');
                                setPendingPhotoFile(null);
                                setCameraStage('live');
                              }}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={openCamera}
                              className="flex-1 min-w-[140px] rounded-lg px-3 py-2 text-sm font-medium shadow-sm"
                              style={{ backgroundColor: '#F07E2D', color: '#FFFFFF' }}
                            >
                              Tomar foto
                            </button>
                            <button
                              type="button"
                              onClick={() => { try { if (photoInputRef.current) photoInputRef.current.click(); } catch (_) {} }}
                              className="rounded-lg px-3 py-2 text-sm font-medium border hover:bg-gray-50"
                            >
                              Subir
                            </button>
                            {photoPreviewUrl ? (
                              <button
                                type="button"
                                onClick={clearPhoto}
                                className="rounded-lg px-3 py-2 text-sm font-medium border border-red-200 text-red-700 bg-red-50 hover:bg-red-100"
                              >
                                Eliminar
                              </button>
                            ) : null}
                          </div>
                          {cameraError ? (
                            <div className="mt-1 text-xs text-red-600">{cameraError}</div>
                          ) : null}
                          <div className="mt-2">
                            {photoPreviewUrl ? (
                              <div className="flex items-center gap-3">
                                <img src={photoPreviewUrl} alt="Foto" className="w-16 h-16 object-cover rounded-lg border" />
                                <a href={photoPreviewUrl} target="_blank" rel="noreferrer" className="text-sm text-indigo-700 hover:underline">
                                  Ver
                                </a>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-500">Sin foto</div>
                            )}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-600 mb-1">Firma</div>
                          <button
                            type="button"
                            onClick={openSignatureModal}
                            className="w-full rounded-lg px-3 py-2 text-sm font-medium shadow-sm inline-flex items-center justify-center gap-2"
                            style={{ backgroundColor: '#04873b', color: '#FFFFFF' }}
                          >
                            <SignatureIcon size={16} />
                            {signaturePreviewUrl ? 'Editar firma' : 'Firmar'}
                          </button>
                          <div className="mt-2">
                            {signaturePreviewUrl ? (
                              <a href={signaturePreviewUrl} target="_blank" rel="noreferrer" className="text-sm text-indigo-700 hover:underline">
                                Ver firma
                              </a>
                            ) : (
                              <div className="text-xs text-gray-500">Sin firma</div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <label className="text-xs text-gray-600">
                          Hora de entrada
                          <input
                            type="datetime-local"
                            value={form.entry_time}
                            onChange={(e) => setForm((p) => ({ ...p, entry_time: e.target.value }))}
                            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                            required
                          />
                        </label>
                        <label className="text-xs text-gray-600">
                          Hora de salida
                          <input
                            type="datetime-local"
                            value={form.exit_time}
                            onChange={(e) => setForm((p) => ({ ...p, exit_time: e.target.value }))}
                            className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-300"
                          />
                        </label>
                      </div>
                      <button
                        type="submit"
                        disabled={saving}
                        className="w-full rounded-lg px-3 py-2 text-sm font-bold shadow-md disabled:opacity-60"
                        style={{ backgroundColor: '#F07E2D', color: '#FFFFFF' }}
                      >
                        {saving ? 'Guardando...' : 'Guardar registro de visitante'}
                      </button>
                    </form>
                    {statusMsg && (
                      <div className={`mt-3 text-xs rounded-lg px-3 py-2 border ${statusMsg.type === 'success' ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-red-600 bg-red-50 border-red-200'}`}>
                        {statusMsg.text}
                      </div>
                    )}
                    </div>
                  </div>
              ),
            },
          ]}
        />
      </div>
      {cameraOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg overflow-hidden">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800">Tomar foto</div>
              <button
                type="button"
                onClick={() => {
                  stopCamera();
                  try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
                  setPendingPhotoUrl('');
                  setPendingPhotoFile(null);
                  setCameraStage('live');
                  setCameraOpen(false);
                }}
                className="text-sm px-3 py-1 rounded border hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
            <div className="p-3">
              {cameraStage === 'preview' && pendingPhotoUrl ? (
                <div>
                  <img src={pendingPhotoUrl} alt="Vista previa" className="w-full rounded-lg border object-contain bg-black" />
                  <div className="mt-3 flex gap-2 justify-between flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
                        setPendingPhotoUrl('');
                        setPendingPhotoFile(null);
                        setCameraStage('live');
                        openCamera();
                      }}
                      className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                    >
                      Tomar otra
                    </button>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          stopCamera();
                          try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
                          setPendingPhotoUrl('');
                          setPendingPhotoFile(null);
                          setCameraStage('live');
                          setCameraOpen(false);
                          // "Capturar" ya deja la foto registrada en el
                          // formulario, así que "Eliminar" también debe
                          // quitarla de ahí, no solo de la vista previa.
                          setForm((p) => ({ ...p, photo: null }));
                          try { if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl); } catch (_) {}
                          setPhotoPreviewUrl('');
                        }}
                        className="px-3 py-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm hover:bg-red-100"
                      >
                        Eliminar
                      </button>
                      <button
                        type="button"
                        onClick={acceptPendingPhoto}
                        className="px-3 py-2 rounded text-sm font-bold shadow-md"
                        style={{ backgroundColor: '#04873b', color: '#FFFFFF' }}
                      >
                        Usar foto
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <video ref={videoRef} playsInline className="w-full rounded-lg bg-black" />
                  {cameraError ? <div className="mt-2 text-xs text-red-600">{cameraError}</div> : null}
                  <div className="mt-3 flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        stopCamera();
                        try { if (pendingPhotoUrl) URL.revokeObjectURL(pendingPhotoUrl); } catch (_) {}
                        setPendingPhotoUrl('');
                        setPendingPhotoFile(null);
                        setCameraStage('live');
                        setCameraOpen(false);
                      }}
                      className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={capturePhoto}
                      className="px-3 py-2 rounded text-white text-sm font-medium shadow-sm"
                      style={{ backgroundColor: '#F07E2D', color: '#FFFFFF' }}
                    >
                      Capturar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {signatureOpen && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl overflow-hidden shadow-xl">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="text-sm font-semibold text-gray-800">Firma</div>
              <button
                type="button"
                onClick={() => { setSignatureOpen(false); }}
                className="text-sm px-3 py-1 rounded border hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>
            <div className="p-3">
              <div className="text-xs text-gray-500 mb-2">Dibuja dentro del recuadro y luego presiona Guardar</div>
              <div className="border-2 border-gray-300 rounded-xl overflow-hidden bg-gray-50 p-2">
                <canvas
                  ref={sigCanvasRef}
                  className="w-full h-80 bg-white touch-none rounded-lg"
                  onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); startSignature(e); }}
                  onPointerMove={moveSignature}
                  onPointerUp={endSignature}
                  onPointerCancel={endSignature}
                />
              </div>
              <div className="mt-3 flex gap-2 justify-between flex-wrap">
                <button
                  type="button"
                  onClick={clearSignature}
                  className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                >
                  Limpiar
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setSignatureOpen(false); }}
                    className="px-3 py-2 rounded border text-sm hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={saveSignature}
                    className="px-3 py-2 rounded text-white text-sm font-medium shadow-sm"
                    style={{ backgroundColor: '#04873b', color: '#FFFFFF' }}
                  >
                    Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
